import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import { Person } from '@eadsysteme/ewsrealestateclient-v3';
import { Role, RoleModel, RoleType, UiRoleType } from '../../../../services/RoleModel';
import { DateUtil } from '../../../../util/DateUtil';
import { PersonDisplayUtil } from '../../../../util/PersonDisplayUtil';
import { StrandContext, StrandNeighbour } from './person-editor-dialog.types';

/** Interner Eintrag: Nachbar plus die Person, an der er hängt (fürs Aussortieren der eigenen Zeilen). */
interface StrandEntry extends StrandNeighbour {
	personId?: string;
}

/** Angrenzende Rollen je Rollentyp; Bewohner und Leerstand bilden einen gemeinsamen Strang. */
@Injectable({ providedIn: 'root' })
export class StrandContextFactory {
	private readonly transloco = inject(TranslocoService);
	private readonly personDisplay = inject(PersonDisplayUtil);

	static isOccupancyStrand(roleType: RoleType | null | undefined): boolean {
		return roleType === UiRoleType.RESIDENT || roleType === UiRoleType.VACANCY;
	}

	/** Der Belegungsstrang wird unter RESIDENT geführt; VACANCY ist kein eigener Strang. */
	static strandTypeOf(roleType: RoleType): RoleType {
		return roleType === UiRoleType.VACANCY ? UiRoleType.RESIDENT : roleType;
	}

	build(roleModels: RoleModel[], personId: string | undefined, roleTypes: RoleType[]): StrandContext[] {
		const strandTypes = [...new Set(roleTypes.map((roleType) => StrandContextFactory.strandTypeOf(roleType)))];
		return strandTypes.map((strandType) => this.buildOne(roleModels, personId, strandType));
	}

	private buildOne(roleModels: RoleModel[], personId: string | undefined, strandType: RoleType): StrandContext {
		const entries = this.collect(roleModels, strandType).sort((a, b) => a.validFrom.valueOf() - b.validFrom.valueOf());
		const own = entries.filter((entry) => !!personId && entry.personId === personId);
		if (own.length === 0) {
			return { roleType: strandType, previous: this.strip(entries.at(-1)) };
		}

		const foreign = entries.filter((entry) => entry.personId !== personId);
		const first = own[0];
		const last = own.at(-1) ?? first;

		return {
			roleType: strandType,
			previous: this.strip([...foreign].reverse().find((entry) => entry.validFrom.isBefore(first.validFrom))),
			next: this.strip(foreign.find((entry) => entry.validFrom.isAfter(last.validFrom))),
		};
	}

	private collect(roleModels: RoleModel[], strandType: RoleType): StrandEntry[] {
		const entries: StrandEntry[] = [];
		for (const roleModel of roleModels ?? []) {
			const name = roleModel.isVacancy ? this.transloco.translate('enum.VACANCY') : this.personDisplay.getLongName(roleModel.person);
			for (const role of roleModel.roles ?? []) {
				if (!role.id || !role.roleType || StrandContextFactory.strandTypeOf(role.roleType) !== strandType) {
					continue;
				}
				entries.push(this.toEntry(role, role.id, name, roleModel.person));
			}
		}
		return entries;
	}

	private toEntry(role: Role, roleId: string, name: string, person?: Person): StrandEntry {
		return {
			roleId,
			roleType: role.roleType,
			name,
			validFrom: role.validFrom ? dayjs(role.validFrom) : DateUtil.minDate,
			validTo: role.validTo && !dayjs(role.validTo).isSame(DateUtil.maxDate, 'day') ? dayjs(role.validTo) : null,
			person,
			personId: person?.id,
		};
	}

	/** Ohne die interne `personId` — die gehört nicht in den Anzeige-Vertrag. */
	private strip(entry?: StrandEntry): StrandNeighbour | undefined {
		if (!entry) {
			return undefined;
		}
		return {
			roleId: entry.roleId,
			roleType: entry.roleType,
			name: entry.name,
			validFrom: entry.validFrom,
			validTo: entry.validTo,
			person: entry.person,
		};
	}
}
